import { createContext, use } from "react";
import { User } from "firebase/auth";

import { IUser, Organisation } from "integrations/firebase/collections";
import { UserRole } from "reedy-data/models";

export interface SessionUser {
  id: string;
  email: User["email"];
  firstName: IUser["givenName"];
  lastName: IUser["familyName"];
  name: string;
  photoUrl: IUser["photoUrl"];
  roles: UserRole[];
  settings: IUser["settings"];
  isSuperAdmin: boolean;
  organisationId: string;
}

export interface ISessionContext {
  user: SessionUser;
  organisation: Organisation;
}

export const createSession = (
  authUser: User | null | undefined,
  userData: IUser | null | undefined,
  organisation: Organisation | null | undefined,
): ISessionContext | null => {
  if (
    !authUser ||
    !userData ||
    !organisation ||
    organisation.deletedAt ||
    // The organisation snapshot lags behind the user record after a switch.
    organisation.id !== userData.organisation?.id
  ) {
    return null;
  }

  const roles = userData.roles ?? [];

  const user = {
    id: authUser.uid,
    email: authUser.email,
    firstName: userData.givenName,
    lastName: userData.familyName,
    name: [userData.givenName, userData.familyName].filter(Boolean).join(" "),
    photoUrl: authUser.photoURL ?? userData.photoUrl,
    roles,
    settings: userData.settings,
    isSuperAdmin: roles.includes(UserRole.SUPER_ADMIN),
    organisationId: organisation.id,
  };

  return { user, organisation };
};

export const SessionContext = createContext<ISessionContext | null>(null);

export const useSession = () => {
  const session = use(SessionContext);

  if (!session) {
    throw new Error("useSession must be used within AppLayout");
  }

  return session;
};
